import { useEffect, useRef, useState } from "react";

import { AnalyticsAttributesProvider } from "../../../components";
import { UiHeaderNavModel } from "../../../models";
import { Logo, ProgressBar } from "../shared";

import { DetailWrapper, Header, InnerHeader, NavBar } from "./BigNav.styles";
import Buttons from "./Buttons";
import TabDetail from "./TabDetail";
import Tabs from "./Tabs";

type BigNavProps = {
    hasScrolled: boolean;
    isLoggedIn?: boolean;
    signUpUrl: string;
    loginUrl: string;
    model: UiHeaderNavModel;
};

const BigNav = ({
    hasScrolled,
    isLoggedIn,
    signUpUrl,
    loginUrl,
    model: {
        id,
        content: {
            goToBoardsText,
            logInText,
            progressBar,
            progressLabel,
            signUpText,
            startTransparent,
            tabs = [],
            useEyebrowLogo
        }
    }
}: BigNavProps) => {
    const [activeTab, setActiveTab] = useState<number | undefined>();

    const innerHeightRef = useRef<HTMLDivElement>(null);
    const [detailHeight, setDetailHeight] = useState(0);

    function escPress(event: KeyboardEvent) {
        if (event.key === "Escape") {
            setActiveTab(undefined);
        }
    }

    //listen for escape key when tab is open
    useEffect(() => {
        if (activeTab !== undefined) {
            window.addEventListener("keydown", escPress);
            document.body.classList.add("prevent-scroll");
        }
        return () => {
            window.removeEventListener("keydown", escPress);
            document.body.classList.remove("prevent-scroll");
        };
    }, [activeTab]);

    //handle layout size changes
    useEffect(() => {
        if (!innerHeightRef.current || typeof ResizeObserver !== "function")
            return;
        const observer = new ResizeObserver(
            (entries: ResizeObserverEntry[]) => {
                setDetailHeight(entries[0]?.contentRect.height ?? 0);
            }
        );
        observer.observe(innerHeightRef.current);
        return () => observer.disconnect();
    }, [innerHeightRef]);

    return (
        <AnalyticsAttributesProvider value={{ eventContainer: "globalNav" }}>
            <Header
                data-open={activeTab !== undefined}
                onClick={() => setActiveTab(undefined)}
                data-testid="bignav"
            >
                <InnerHeader
                    data-active={hasScrolled || activeTab !== undefined}
                    data-start-transparent={startTransparent?.toString()}
                    onClick={(e) => e.stopPropagation()}
                >
                    <NavBar>
                        <Logo id={id} eyebrow={useEyebrowLogo} />

                        {tabs && (
                            <Tabs
                                activeTab={activeTab}
                                setActiveTab={setActiveTab}
                                tabs={tabs}
                            />
                        )}

                        <Buttons
                            goToBoardsText={goToBoardsText}
                            isLoggedIn={isLoggedIn}
                            logInText={logInText}
                            signUpText={signUpText}
                            signUpUrl={signUpUrl}
                            loginUrl={loginUrl}
                            uuid={id}
                        />

                        {progressBar && (
                            <ProgressBar progressLabel={progressLabel} />
                        )}
                    </NavBar>

                    {tabs.filter((tab) => tab.type === "uiHeaderNavTab") && (
                        <DetailWrapper style={{ height: detailHeight }}>
                            <div ref={innerHeightRef}>
                                {tabs.map(
                                    (tab, index) =>
                                        tab.type === "uiHeaderNavTab" && (
                                            <div
                                                key={index}
                                                style={{
                                                    display:
                                                        activeTab === index
                                                            ? "block"
                                                            : "none"
                                                }}
                                            >
                                                <TabDetail
                                                    tab={tab}
                                                    active={activeTab === index}
                                                />
                                            </div>
                                        )
                                )}
                            </div>
                        </DetailWrapper>
                    )}
                </InnerHeader>
            </Header>
        </AnalyticsAttributesProvider>
    );
};

export default BigNav;
